import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Chip,
  Avatar,
  Grid,
  CardHeader,
  IconButton,
  Tooltip,
  InputAdornment,
  TextField,
  CircularProgress
} from "@mui/material";
import {
  People as PeopleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon
} from "@mui/icons-material";

import { getHousekeepingStaff } from "../../services/api";
import { useSnackbar } from "notistack";

export const StaffMaster = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const res = await getHousekeepingStaff();
      setStaff(res.data || []);
    } catch (err) {
      enqueueSnackbar("Failed to load staff", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredStaff = staff.filter((s) =>
    `${s.first_name} ${s.last_name} ${s.designation} ${s.department} ${s.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const getRoleColor = (role) => {
    const roleColors = {
      'supervisor': 'primary',
      'manager': 'secondary',
      'cleaner': 'success',
      'inspector': 'warning',
      'coordinator': 'info'
    };
    return roleColors[role?.toLowerCase()] || 'default';
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <PeopleIcon sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Housekeeping Staff
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Manage and view all housekeeping team members
              </Typography>
            </Grid>
            <Grid item>
              <Chip
                icon={<PeopleIcon />}
                label={`${staff.length} Staff Members`}
                variant="outlined"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  borderColor: 'white',
                  fontSize: '1.1rem',
                  py: 2,
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search staff by name, role, department, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.1rem',
                    '& input': {
                      padding: '12px 14px',
                      fontSize: '1.1rem'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
              <Tooltip title="Filter staff">
                <IconButton 
                  sx={{ 
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    fontSize: '1.1rem'
                  }}
                >
                  <FilterIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Staff Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
              Staff Directory
            </Typography>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
        
        <CardContent sx={{ p: 0 }}>
          <TableContainer 
            component={Paper} 
            sx={{ 
              borderRadius: 2,
              border: 'none',
              boxShadow: 'none'
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: 'white', 
                    fontSize: '1.2rem',
                    py: 3,
                    border: 'none'
                  }}>
                    Staff Member
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: 'white', 
                    fontSize: '1.2rem',
                    py: 3,
                    border: 'none'
                  }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: 'white', 
                    fontSize: '1.2rem',
                    py: 3,
                    border: 'none'
                  }}>
                    Department
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: 'white', 
                    fontSize: '1.2rem',
                    py: 3,
                    border: 'none'
                  }}>
                    Contact
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: 'white', 
                    fontSize: '1.2rem',
                    py: 3,
                    border: 'none'
                  }}>
                    Employee ID
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
                      <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>
                        Loading staff data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredStaff.length > 0 ? (
                  filteredStaff.map((s, index) => (
                    <TableRow
                      key={s._id}
                      hover
                      sx={{
                        transition: "all 0.3s ease",
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        },
                        '&:last-child td': { borderBottom: 'none' }
                      }}
                    >
                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        py: 3,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 56,
                              height: 56,
                              backgroundColor: 'primary.main',
                              fontWeight: 600,
                              fontSize: '1.2rem'
                            }}
                          >
                            {getInitials(s.first_name, s.last_name)}
                          </Avatar>
                          <Box>
                            <Typography 
                              variant="h6" 
                              fontWeight={700}
                              sx={{ fontSize: '1.2rem', mb: 0.5 }}
                            >
                              {s.first_name} {s.last_name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: '1rem',
                                color: 'text.secondary'
                              }}
                            >
                              {s.designation || "Staff Member"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        py: 3,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Chip
                          label={s.designation || "N/A"}
                          color={getRoleColor(s.designation)}
                          variant="filled"
                          sx={{ 
                            fontSize: '1rem',
                            fontWeight: 600,
                            py: 1,
                            minWidth: 100
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        py: 3,
                        fontWeight: 600,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}>
                        {s.department || "N/A"}
                      </TableCell>

                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        py: 3,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {s.mobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                              <Typography sx={{ fontSize: '1.1rem' }}>
                                {s.mobile}
                              </Typography>
                            </Box>
                          )}
                          {s.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                              <Typography sx={{ fontSize: '1.1rem' }}>
                                {s.email}
                              </Typography>
                            </Box>
                          )}
                          {!s.mobile && !s.email && (
                            <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        py: 3,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BadgeIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                          <Typography 
                            fontWeight={600}
                            sx={{ fontSize: '1.1rem' }}
                          >
                            {s.emp_id || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <PeopleIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                        <Typography variant="h5" sx={{ fontSize: '1.5rem', mb: 1 }}>
                          {searchTerm ? "No matching staff found" : "No staff members"}
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                          {searchTerm 
                            ? "Try adjusting your search terms" 
                            : "Staff directory is currently empty"
                          }
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Summary Footer */}
      {!loading && filteredStaff.length > 0 && (
        <Card sx={{ mt: 3, borderRadius: 3, backgroundColor: 'success.light' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography 
              align="center" 
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'success.dark'
              }}
            >
              Showing {filteredStaff.length} of {staff.length} staff members
              {searchTerm && ` matching "${searchTerm}"`}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};